import axios, { AxiosResponse, AxiosError } from "axios";
import {
  COOKIE_ACCESS_TOKEN_NAME,
  COOKIE_REFRESH_TOKEN_NAME,
  COOKIE_MANAGER_ACCESS_TOKEN_NAME,
  COOKIE_MANAGER_REFRESH_TOKEN_NAME,
  COOKIE_OPTIONS
} from "constants/common";
import { API_URL } from "constants/config";
import { STATUS_CODES } from "constants/statusCodes";
import { Cookies } from "react-cookie";

import { ApiError, CustomAxiosRequestConfig } from "./api.types";

const cookies = new Cookies();

interface CreateApiInstanceParams {
  accessCookieName: string;
  refreshCookieName: string;
  refresh: (refreshToken: string) => Promise<string>;
}

const createApiInstance = ({
  accessCookieName,
  refreshCookieName,
  refresh
}: CreateApiInstanceParams) => {
  const apiInstance = axios.create({
    baseURL: API_URL,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  });

  apiInstance.interceptors.request.use(
    (config: CustomAxiosRequestConfig) => {
      const token =
        cookies.get(accessCookieName) ||
        cookies.get(COOKIE_ACCESS_TOKEN_NAME) ||
        cookies.get(COOKIE_MANAGER_ACCESS_TOKEN_NAME);
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    // oxlint-disable-next-line promise/no-promise-in-callback
    (error: AxiosError) => Promise.reject(error)
  );

  apiInstance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => response.data,
    async (error: AxiosError<ApiError>) => {
      const originalRequest = error.config as CustomAxiosRequestConfig;
      const refreshToken = cookies.get(refreshCookieName);

      const isAuthFailure =
        error.response && error.response.status === STATUS_CODES.UNAUTHORIZED;

      if (
        isAuthFailure &&
        // oxlint-disable-next-line no-underscore-dangle
        !originalRequest?._retry &&
        refreshToken &&
        originalRequest
      ) {
        // oxlint-disable-next-line no-underscore-dangle
        originalRequest._retry = true;
        try {
          const accessJwt = await refresh(refreshToken);
          cookies.set(accessCookieName, accessJwt, COOKIE_OPTIONS);
          return await apiInstance(originalRequest);
        } catch {
          cookies.remove(accessCookieName, COOKIE_OPTIONS);
          cookies.remove(refreshCookieName, COOKIE_OPTIONS);
          // oxlint-disable-next-line promise/no-promise-in-callback
          return await Promise.reject(error);
        }
      }

      // oxlint-disable-next-line promise/no-promise-in-callback
      return await Promise.reject(error);
    }
  );

  return apiInstance;
};

const refreshAdminAccess = async (refreshToken: string): Promise<string> => {
  const response = await axios.post(
    `${API_URL}/admin/auth/access-jwt/refresh`,
    undefined,
    { headers: { Authorization: `Bearer ${refreshToken}` } }
  );
  return response.data.accessJwt;
};

const refreshManagerAccess = async (refreshToken: string): Promise<string> => {
  // Use the unified refresh endpoint — it verifies the refresh JWT against
  // admin / delivery_company / company_manager secrets and returns a new access JWT
  // tagged with the right role.
  const response = await axios.post(
    `${API_URL}/admin/auth/access-jwt/refresh`,
    undefined,
    { headers: { Authorization: `Bearer ${refreshToken}` } }
  );
  return response.data.accessJwt;
};

export const instance = createApiInstance({
  accessCookieName: COOKIE_ACCESS_TOKEN_NAME,
  refreshCookieName: COOKIE_REFRESH_TOKEN_NAME,
  refresh: refreshAdminAccess
});

export const managerInstance = createApiInstance({
  accessCookieName: COOKIE_MANAGER_ACCESS_TOKEN_NAME,
  refreshCookieName: COOKIE_MANAGER_REFRESH_TOKEN_NAME,
  refresh: refreshManagerAccess
});
